import { isW1Shell } from '../../utils/w1Flag';
/**
 * Sidebar navigation model (W1 S0b extraction from App.tsx).
 *
 * Task 1.3: [Home] [Operations] [Data Ingestion] [Analytics]
 * Settings, Profile, Preferences, Sign Out moved to User dropdown.
 *
 * W1a: grouped information architecture (see the Management Portal redesign plan,
 * documentation/…Redesign…Plan.docx §4). Every routed page is reachable from the
 * sidebar — this closes the CRITICAL findings that Monitor and the entire CRM
 * workspace were unreachable except by typed URL. Pages that the later waves fold
 * into tabbed hubs (Ingestion, Backups, Users, Costs+Pricing, CRM) are listed
 * individually here for now; W4 consolidates them.
 */
import type { ComponentType } from 'react';
import {
  Home,
  Monitor as MonitorIcon,
  FileText,
  LifeBuoy,
  Activity,
  HardDrive,
  Archive,
  Server,
  Rocket,
  Package,
  Layout as LayoutIcon,
  BookOpen,
  Users,
  Shield,
  Key,
  DollarSign,
  AlertOctagon,
  Bell,
  ListChecks,
  ClipboardCheck,
  CalendarClock,
  Siren,
  Workflow,
  Layers,
  Database,
} from 'lucide-react';

export interface NavLeaf {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
}

export interface NavGroup {
  // null section = top-level items rendered without a header (e.g. Overview)
  section: string | null;
  items: NavLeaf[];
}

export const navGroups: NavGroup[] = [
  { section: null, items: [
    { name: 'Overview', href: '/', icon: Home },
  ] },
  { section: 'Observe', items: [
    { name: 'Monitor', href: '/monitor', icon: MonitorIcon },
    { name: 'Logs', href: '/logs', icon: FileText },
    { name: 'Support Tickets', href: '/support', icon: LifeBuoy },
    { name: 'Ingestion Activity', href: '/monitor/activity', icon: Activity },
  ] },
  { section: 'Data', items: [
    { name: 'Data Stores', href: '/datastores', icon: HardDrive },
    { name: 'Ingestion', href: '/ingestion', icon: Activity },
    { name: 'Backups', href: '/backups', icon: Archive },
  ] },
  { section: 'Environments', items: [
    { name: 'Environments', href: '/environments', icon: Server },
    { name: 'Releases', href: '/releases', icon: Rocket },
    { name: 'Promotion Packages', href: '/promotion-packages', icon: Package },
  ] },
  { section: 'Platform', items: [
    { name: 'Platform Services', href: '/services', icon: Server },
    { name: 'Portals', href: '/portals', icon: LayoutIcon },
    { name: 'Developer VMs', href: '/developer-vms', icon: MonitorIcon },
    { name: 'Knowledge', href: '/knowledge', icon: BookOpen },
  ] },
  { section: 'Access & Security', items: [
    { name: 'Users & Orgs', href: '/users-orgs', icon: Users },
    { name: 'Access Control', href: '/access', icon: Shield },
    { name: 'Encryption Keys', href: '/key-management', icon: Key },
  ] },
  { section: 'Identity (W3)', items: [
    { name: 'Identity Users', href: '/administration/identity/users', icon: Users },
    { name: 'Identity Invitations', href: '/administration/identity/invitations', icon: Users },
    { name: 'Identity Access', href: '/administration/identity/access', icon: Shield },
    { name: 'Identity Reviews', href: '/administration/identity/reviews', icon: Shield },
    { name: 'My Preferences', href: '/my-account/preferences', icon: Users },
  ] },
  { section: 'Billing', items: [
    { name: 'Costs & Pricing', href: '/billing', icon: DollarSign },
  ] },
  { section: 'CRM', items: [
    { name: 'CRM Workspace', href: '/crm', icon: Users },
  ] },
  // W5 Stream 1 (Story #5352, Task #5424) — appended at the end, per the
  // append-only nav rule (never interleaved with the sections above).
  { section: 'Operations', items: [
    { name: 'Command Centre', href: '/operations', icon: AlertOctagon },
    { name: 'Attention', href: '/operations/attention', icon: Bell },
    { name: 'Jobs', href: '/operations/jobs', icon: ListChecks },
    { name: 'Approvals', href: '/operations/approvals', icon: ClipboardCheck },
    { name: 'Schedules', href: '/operations/schedules', icon: CalendarClock },
    { name: 'Incidents', href: '/operations/incidents', icon: Siren },
  ] },
  // W5 Stream 2 (Story #5352, Tasks #5434-#5438) — appended at the end, per
  // the append-only nav rule (never interleaved with the sections above).
  { section: 'Data Flows', items: [
    { name: 'Overview', href: '/operations/data-flows', icon: Workflow },
    { name: 'Jobs', href: '/operations/data-flows/jobs', icon: Layers },
    { name: 'Sources', href: '/operations/data-flows/sources', icon: Database },
    { name: 'Schedules', href: '/operations/data-flows/schedules', icon: CalendarClock },
    { name: 'Activity (AEMO)', href: '/operations/data-flows/activity', icon: Activity },
  ] },
  // W5 Stream 3 — appended, never interleaved with established navigation.
  { section: 'Recovery', items: [
    { name: 'Backups', href: '/backups', icon: Archive },
    { name: 'Recovery Points', href: '/backups?recovery_view=catalogue', icon: Database },
    { name: 'Restore Plans', href: '/backups?recovery_view=recover', icon: ListChecks },
    { name: 'Vaults & DR', href: '/backups?tab=vaults', icon: Shield },
    { name: 'Rehearsals', href: '/backups?tab=rehearsals', icon: Activity },
    { name: 'Evidence', href: '/backups?tab=evidence', icon: FileText },
  ] },
];

// Flat list of every nav destination — used by the route-manifest test to assert
// each sidebar link resolves to a real route.
export const allNavHrefs: string[] = navGroups.flatMap((g) => g.items.map((i) => i.href));

// W1 shell (Bug #5625, mockup G01_global_shell_global_shell.png) — the eight
// domain items shown when isW1Shell() is on, replacing the legacy 12-section/
// 43-item navGroups above. Flat list, no section headers, in the mockup's
// exact order. Help and My account are NOT here — the mockup shows them in
// the sidebar footer, rendered separately by Sidebar.tsx.
export const w1TopNav: NavLeaf[] = [
  { name: 'Home', href: '/home', icon: Home },
  { name: 'Environments', href: '/environments', icon: Server },
  { name: 'Portals', href: '/portals', icon: LayoutIcon },
  { name: 'Releases', href: '/releases', icon: Rocket },
  { name: 'Operations', href: '/operations', icon: AlertOctagon },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Administration', href: '/administration', icon: Shield },
  { name: 'Knowledge', href: '/knowledge', icon: BookOpen },
];

export const w1TopNavHrefs: string[] = w1TopNav.map((i) => i.href);

/** Read the same feature switches as App. This is availability, never authorisation. */
export function getNavigationContext(pathname: string, search: string): import('../../navigation/routeBindings').NavigationContext {
  const runtime = typeof window === 'undefined' ? undefined : (window as unknown as { SECUREEMBED_CONFIG?: { PORTALS_WORKSPACE_ENABLED?: boolean } }).SECUREEMBED_CONFIG;
  return { pathname, search, features: {
    shell: isW1Shell(),
    environmentsV2: import.meta.env.VITE_ENV_RELEASES_V2 === 'true',
    portalsWorkspace: runtime?.PORTALS_WORKSPACE_ENABLED === true,
  } };
}
